import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Plus, Trash2, AlertTriangle, RefreshCw, Pencil } from 'lucide-react'
import { formatDateTimeRange } from '@/lib/date-utils'
import { QuoteItemList, QuoteItemListContainer } from './quote-item-table'
import { QuoteEventSheet } from './quote-event-sheet'
import type { Timestamp } from '@bufbuild/protobuf/wkt'

/**
 * Form-level representation of a quote event
 * Used for managing events in the quote builder before saving
 */
export interface QuoteEventFormValue {
  /** Temporary client-side ID for tracking (not persisted) */
  tempId: string
  /** Server-assigned ID (only present when editing existing events) */
  id?: string
  eventDate?: Date
  eventLength?: number
  eventGuests?: number
  eventTimezone?: string
  eventLocation?: string
  baristaCount?: number
  cartCount?: number
}

// Shown before any events exist — nothing has been copied yet, so this is forward-looking.
const QUOTE_EVENTS_EMPTY_EXPLAINER =
  "Events added here belong to this quote, not the lead. Whatever's on the quote when the client accepts is what becomes their real events."

// Shown once the quote has events — may or may not have come from "Replace with Lead Events".
const QUOTE_EVENTS_EXPLAINER =
  'These events belong to this quote. They may have been copied from the lead once, and this copy is what becomes the real events when the client accepts.'

/** Muted second line of an event row: only the details actually filled in. */
function formatQuoteEventFacts(event: QuoteEventFormValue): string {
  const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`
  const facts = [
    event.eventLength
      ? plural(event.eventLength % 1 === 0 ? event.eventLength : Number(event.eventLength.toFixed(1)), 'hour')
      : 'Duration not set',
  ]
  if (event.eventGuests) facts.push(plural(event.eventGuests, 'guest'))
  if (event.baristaCount) facts.push(plural(event.baristaCount, 'barista'))
  if (event.cartCount) facts.push(plural(event.cartCount, 'cart'))
  return facts.join(' · ')
}

interface QuoteEventsSectionProps {
  events: QuoteEventFormValue[]
  onEventsChange: (events: QuoteEventFormValue[]) => void
  disabled?: boolean
  /** Optional inquiry events to copy from */
  inquiryEvents?: Array<{
    eventDate?: Timestamp
    eventLength?: number
    eventGuests?: number
    eventTimezone?: string
    eventLocation?: string
    baristaCount?: number
    cartCount?: number
  }>
}

/**
 * QuoteEventsSection - Component for managing multiple events in a quote
 *
 * Features:
 * - `Events Included` list: one compact row per event, on the same row grammar
 *   as the Line Items card so the builder reads as one surface
 * - Add/Edit/Delete events inline from the row, no disclosure to open first
 * - "Replace with Lead Events" button to overwrite with the lead's events (confirms first if the quote already has events)
 * - Events are managed in form state (saved when quote is saved)
 */
export function QuoteEventsSection({
  events,
  onEventsChange,
  disabled = false,
  inquiryEvents,
}: QuoteEventsSectionProps) {
  const [editingEvent, setEditingEvent] = useState<{ event: QuoteEventFormValue; index: number } | null>(null)
  const [createSheetOpen, setCreateSheetOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [eventToDelete, setEventToDelete] = useState<QuoteEventFormValue | null>(null)
  const [copyConfirmOpen, setCopyConfirmOpen] = useState(false)

  // Cannot delete if only one event remains
  const canDelete = events.length > 1

  const handleDeleteClick = (event: QuoteEventFormValue) => {
    setEventToDelete(event)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (!eventToDelete) return
    const updatedEvents = events.filter((e) => e.tempId !== eventToDelete.tempId)
    onEventsChange(updatedEvents)
    setDeleteDialogOpen(false)
    setEventToDelete(null)
  }

  const handleCreateEvent = (newEvent: Omit<QuoteEventFormValue, 'tempId'>) => {
    const eventWithId: QuoteEventFormValue = {
      ...newEvent,
      tempId: crypto.randomUUID(),
    }
    onEventsChange([...events, eventWithId])
    setCreateSheetOpen(false)
  }

  const handleUpdateEvent = (updatedEvent: QuoteEventFormValue) => {
    const updatedEvents = events.map((e) =>
      e.tempId === updatedEvent.tempId ? updatedEvent : e
    )
    onEventsChange(updatedEvents)
    setEditingEvent(null)
  }

  const handleCopyFromInquiry = () => {
    if (!inquiryEvents || inquiryEvents.length === 0) return

    const copiedEvents: QuoteEventFormValue[] = inquiryEvents.map((inquiryEvent) => {
      // Convert proto Timestamp to Date
      let eventDate: Date | undefined
      if (inquiryEvent.eventDate) {
        const seconds = typeof inquiryEvent.eventDate.seconds === 'bigint'
          ? Number(inquiryEvent.eventDate.seconds)
          : inquiryEvent.eventDate.seconds
        eventDate = new Date(seconds * 1000)
      }

      return {
        tempId: crypto.randomUUID(),
        eventDate,
        eventLength: inquiryEvent.eventLength,
        eventGuests: inquiryEvent.eventGuests,
        eventTimezone: inquiryEvent.eventTimezone,
        eventLocation: inquiryEvent.eventLocation,
        baristaCount: inquiryEvent.baristaCount,
        cartCount: inquiryEvent.cartCount,
      }
    })

    onEventsChange(copiedEvents)
  }

  const handleCopyFromInquiryClick = () => {
    if (events.length > 0) {
      setCopyConfirmOpen(true)
      return
    }
    handleCopyFromInquiry()
  }

  const handleCopyConfirm = () => {
    handleCopyFromInquiry()
    setCopyConfirmOpen(false)
  }

  // Get the first event's values to use as defaults for new events
  const firstEvent = events[0]

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          Events Included
          {events.length > 0 && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {events.length} {events.length === 1 ? 'event' : 'events'}
            </span>
          )}
        </h3>
        <div className="flex flex-wrap items-center gap-1">
          {inquiryEvents && inquiryEvents.length > 0 && (
            <Button
              type="button"
              onClick={handleCopyFromInquiryClick}
              variant="ghost"
              size="sm"
              disabled={disabled}
            >
              <RefreshCw className="size-3.5" />
              Replace from lead ({inquiryEvents.length})
            </Button>
          )}
          <Button
            type="button"
            onClick={() => setCreateSheetOpen(true)}
            variant="outline"
            size="sm"
            disabled={disabled}
          >
            <Plus className="size-3.5" />
            Add Event
          </Button>
        </div>
      </div>

      <QuoteItemListContainer>
        {events.length === 0 ? (
          <button
            type="button"
            onClick={() => setCreateSheetOpen(true)}
            disabled={disabled}
            className="flex w-full items-center gap-2 px-3 py-3 text-left text-sm text-muted-foreground hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50"
          >
            <Plus className="size-4 shrink-0" aria-hidden="true" />
            Add the first event — date, guests and location
          </button>
        ) : (
          <QuoteItemList>
            {events.map((event, index) => (
              <li key={event.id || event.tempId}>
                <div className="flex items-start gap-2 px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      {event.eventDate
                        ? formatDateTimeRange(event.eventDate, event.eventLength, event.eventTimezone)
                        : 'Date not set'}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {formatQuoteEventFacts(event)}
                    </p>
                    {event.eventLocation && (
                      <p className="mt-0.5 break-words text-xs text-muted-foreground">
                        {event.eventLocation}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 text-muted-foreground"
                      aria-label={`Edit event ${index + 1}`}
                      onClick={() => setEditingEvent({ event, index })}
                      disabled={disabled}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 text-muted-foreground hover:text-destructive"
                      aria-label={`Remove event ${index + 1}`}
                      disabled={!canDelete || disabled}
                      title={canDelete ? 'Remove event' : 'A quote keeps at least one event'}
                      onClick={() => handleDeleteClick(event)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </QuoteItemList>
        )}
      </QuoteItemListContainer>

      <p className="text-xs text-muted-foreground">
        {events.length === 0 ? QUOTE_EVENTS_EMPTY_EXPLAINER : QUOTE_EVENTS_EXPLAINER}
      </p>

      {/* Create Event Sheet */}
      <QuoteEventSheet
        mode="create"
        open={createSheetOpen}
        onOpenChange={setCreateSheetOpen}
        onSave={handleCreateEvent}
        defaultEventLength={firstEvent?.eventLength}
        defaultEventGuests={firstEvent?.eventGuests}
        defaultEventTimezone={firstEvent?.eventTimezone}
        defaultEventLocation={firstEvent?.eventLocation}
        defaultBaristaCount={firstEvent?.baristaCount}
        defaultCartCount={firstEvent?.cartCount}
        disabled={disabled}
      />

      {/* Edit Event Sheet */}
      {editingEvent && (
        <QuoteEventSheet
          mode="edit"
          event={editingEvent.event}
          eventIndex={editingEvent.index}
          open={!!editingEvent}
          onOpenChange={(open) => {
            if (!open) setEditingEvent(null)
          }}
          onSave={handleUpdateEvent}
          disabled={disabled}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Event
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this event? This will be reflected when you save the quote.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              variant="destructive"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Replace-from-Lead Confirmation Dialog */}
      <AlertDialog open={copyConfirmOpen} onOpenChange={setCopyConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Replace Quote Events
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will replace the {events.length} event{events.length !== 1 ? 's' : ''} on this quote with {inquiryEvents?.length ?? 0} event{(inquiryEvents?.length ?? 0) !== 1 ? 's' : ''} from the lead. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCopyConfirm}
              variant="destructive"
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </section>
  )
}
